/**
 * The published treasury rule, and the arithmetic that implements it.
 *
 * This module is the single source of truth for three things that must never
 * drift apart: the rule text shown on `/honest-numbers`, the weights the
 * `--plan` mode of `scripts/record-sweep.ts` prints for a human to execute, and
 * the explorer link format used everywhere a sweep tx is rendered.
 *
 * Nothing here moves money. The sweep is a human action; this only computes
 * what the published rule says the split should be, and records it afterwards.
 */

/** Blockscout instance for chain 4663 (Robinhood Chain). */
export const EXPLORER_BASE = 'https://robinhoodchain.blockscout.com';

export function explorerTxUrl(txHash: string): string {
  return `${EXPLORER_BASE}/tx/${txHash}`;
}

export function explorerAddressUrl(address: string): string {
  return `${EXPLORER_BASE}/address/${address}`;
}

/**
 * Fixed, pre-published allocation. Dev spec §6. Order matters: a skipped leg
 * rolls its weight onto the next leg in this order.
 */
export const SWEEP_LEGS: ReadonlyArray<{ symbol: string; weight: number }> = [
  { symbol: 'NVDA', weight: 0.4 },
  { symbol: 'SPCX', weight: 0.35 },
  { symbol: 'SPY', weight: 0.25 },
];

/** A leg whose reference pool holds less than this is skipped and rolled on. */
export const MIN_LEG_LIQ_USD = 500_000;

/**
 * The rule as published. Changed only with a public note, and only after the
 * change is posted — never retroactively.
 */
export const SWEEP_RULE_TEXT =
  'Creator fees are swept every Sunday at 00:00 UTC from one public wallet and ' +
  'spent on a fixed, pre-published split: 40% NVDA, 35% SPCX, 25% SPY. A leg whose ' +
  'reference pool holds less than $500,000 of liquidity at sweep time is skipped and ' +
  'its share rolls onto the next leg in that order. Every buy is recorded here with ' +
  'its transaction hash. No presale, no team allocation. The rule changes only with a ' +
  'public note posted before the sweep it applies to.';

export interface PlanLeg {
  symbol: string;
  /** Published weight for this leg, before any roll. */
  targetWeight: number;
  /** Weight actually applied, after rolls in or out. 0 when skipped. */
  appliedWeight: number;
  usd: number;
  /** Reference pool liquidity used for the decision; null when unknown. */
  liqUsd: number | null;
  status: 'buy' | 'skipped' | 'unknown-liquidity';
}

export interface SweepPlan {
  balanceUsd: number;
  legs: PlanLeg[];
  /** Left over only when every leg was skipped. Held in USDG until next sweep. */
  unallocatedUsd: number;
}

/**
 * Apply the published rule to a USD balance.
 *
 * `liquidity` maps symbol → reference-pool liquidity in USD, or null when we
 * could not read it. Unknown liquidity does **not** skip a leg: skipping on
 * missing data would silently change the published split because of our own
 * outage. It buys, and the plan flags the row so the human can check first.
 */
export function planSweep(
  balanceUsd: number,
  liquidity: Readonly<Record<string, number | null>> = {},
): SweepPlan {
  const legs: PlanLeg[] = [];
  let carry = 0;

  for (const leg of SWEEP_LEGS) {
    const liq = liquidity[leg.symbol] ?? null;
    const skip = liq !== null && liq < MIN_LEG_LIQ_USD;
    const weight = leg.weight + carry;

    if (skip) {
      carry = weight;
      legs.push({
        symbol: leg.symbol,
        targetWeight: leg.weight,
        appliedWeight: 0,
        usd: 0,
        liqUsd: liq,
        status: 'skipped',
      });
      continue;
    }

    carry = 0;
    legs.push({
      symbol: leg.symbol,
      targetWeight: leg.weight,
      appliedWeight: weight,
      usd: round2(balanceUsd * weight),
      liqUsd: liq,
      status: liq === null ? 'unknown-liquidity' : 'buy',
    });
  }

  // The last leg was skipped: its weight has nowhere forward to go, so it rolls
  // back onto the last leg that is still buying.
  if (carry > 0) {
    const last = [...legs].reverse().find((l) => l.status !== 'skipped');
    if (last) {
      last.appliedWeight += carry;
      last.usd = round2(balanceUsd * last.appliedWeight);
      carry = 0;
    }
  }

  return { balanceUsd: round2(balanceUsd), legs, unallocatedUsd: round2(balanceUsd * carry) };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
